import { Hub } from '@aws-amplify/core';
import type { ComposerTranslation } from 'vue-i18n';
import isEqual from 'lodash/isEqual';
import {
    type AddNotificationPayload, type BackendNotification,
    type Notification,
    type NotificationActionProducer, NotificationActionType, type Payload, type ShowToastPayload
} from '../types/notifications';
import { useBackendEventsStore } from './backendEventsStore';


export const useNotificationsStore = defineStore('notificationsStore', () => {
    const eventsStore = useBackendEventsStore();
    const registeredHandlers = [] as {channel: string, actionProducer: NotificationActionProducer}[];

    const notifications = ref<Notification[]>([]);
    let showToast: (detail: string, summary: string) => void;
    let showErrorToast: (detail: string) => void;
    let showWarningToast: (detail: string, summary?: string) => void;
    let t: ComposerTranslation;
    const notificationComponents = {} as Record<string, Component>;

    const actionsHandlers = {
        [NotificationActionType.SHOW_TOAST]: (payload: Payload) => {
            const pl = payload as ShowToastPayload;
            const summary = pl.summary ?? t('notifications.announcement');
            if (pl.severity === 'error') {
                showErrorToast(pl.detail);
            } else if (pl.severity === 'warn') {
                showWarningToast(pl.detail, summary);
            } else {
                showToast(pl.detail, summary);
            }
        },
        [NotificationActionType.ADD_NOTIFICATION]: (payload: Payload) => {
            const pl = payload as AddNotificationPayload;

            notifications.value = notifications.value
                .filter(n => !isEqual(n.keys, pl.notification.keys));

            notifications.value.push(pl.notification);
        }
    };

    function addHandler (channel: string, actionProducer: NotificationActionProducer) {
        registeredHandlers.push({ channel, actionProducer });
    }

    function addNotificationComponent (name: string, component: Component) {
        notificationComponents[name] = component;
    }

    function getNotificationComponent (name: string):Component {
        return notificationComponents[name]!;
    }

    function acknowledge (notification: Notification) {
        notifications.value = notifications.value
            .filter(n => n.timestamp !== notification.timestamp && n.eventName !== notification.eventName);
    }

    function acknowledgeAll () {
        notifications.value = [];
    }

    function addSubscriptions () {
        for (const h of registeredHandlers) {
            Hub.listen(h.channel, (ev) => {
                const action = h.actionProducer(ev.payload.data as unknown as BackendNotification);

                if (action) {
                    actionsHandlers[action.actionType](action.payload);
                }
            });
        }
    }

    /**
     * From not-components we cannot access i18n, so it has to be provided on notifications handling initialization
     */
    async function initialize (
        showToastFunc: (detail: string, summary: string) => void,
        translationFunc: ComposerTranslation,
        showErrorToastFunc?: (detail: string) => void,
        showWarningToastFunc?: (detail: string, summary?: string) => void
    ) {
        showToast = showToastFunc;
        showErrorToast = showErrorToastFunc ?? (detail => showToastFunc(detail, 'Error'));
        showWarningToast = showWarningToastFunc ?? ((detail, summary) => showToastFunc(detail, summary ?? 'Warning'));
        t = translationFunc;

        await ensureSubscription();
        addSubscriptions();
    }

    async function ensureSubscription () {
        await eventsStore.ensureSubscription();
    }

    function addFunctionHandler (channel: string, func: (notification: BackendNotification) => void): () => void {
        return Hub.listen(channel, (ev) => {
            func(ev.payload.data as unknown as BackendNotification);
        });
    }

    return { initialize, notifications, addHandler, acknowledge, acknowledgeAll, addNotificationComponent, getNotificationComponent, ensureSubscription, addFunctionHandler };
});
